/**
 * GetEquity Invest ("My Investments") — derived helpers.
 *
 * Pure functions that turn raw holdings (TokenResponseModel), performance
 * series points and trade orders (TransactionPayloadModel) into the values the
 * Invest UI renders. Framework-free so the page and its child components share
 * one source of truth. See the Invest build spec §4–§9.
 *
 * Currency/format primitives are reused from the Exchange util so both the
 * primary-market (Invest) and secondary-market (Exchange) pages format money
 * identically.
 */

import { currencySymbol, currencyName, compact, money } from "@/utils/exchange.js";

export { currencySymbol, currencyName, compact, money };

const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/* ------------------------------------------------------------------ *
 * Currency scoping
 * ------------------------------------------------------------------ */

// The page is always scoped to one of these three, toggled on the summary
// card. Default is NGN (spec §4).
export const INVEST_CURRENCIES = ["NGN", "USD", "KES"];
export const DEFAULT_CURRENCY = "NGN";

export const tokenCurrency = (token) => token.currency || DEFAULT_CURRENCY;

export const holdingShares = (token) => num(token.balance);
export const holdingCashValue = (token) => num(token.usd_value);

// Non-null entity → ESOP / stock holding (routes to the stock-list screen).
export const isEntityHolding = (token) => token.entity != null;

/**
 * Active holdings for the given currency: matches currency and has a non-zero
 * balance (spec §6 — "excludes empty/zero tokens").
 */
export const currencyHoldings = (tokens, currency) => {
  const cur = (currency || "").toUpperCase();
  return (tokens || []).filter(
    (t) => tokenCurrency(t).toUpperCase() === cur && holdingShares(t) > 0
  );
};

export const totalShares = (holdings) =>
  (holdings || []).reduce((sum, t) => sum + holdingShares(t), 0);

export const totalCashValue = (holdings) =>
  (holdings || []).reduce((sum, t) => sum + holdingCashValue(t), 0);

/* ------------------------------------------------------------------ *
 * Portfolio allocation (spec §5b)
 * ------------------------------------------------------------------ */

// One family, the app's blues and greys, largest share first: the slices stay apart by
// lightness (blue, navy, pale blue, grey, ...) rather than by competing hues.
export const ALLOCATION_PALETTE = [
  "#4E84FF",
  "#06206B",
  "#A9C3FF",
  "#8791A7",
  "#D6E2FF",
  "#C4CAD6",
  "#2F4A8A",
];

/**
 * Group Σ cash value per investment_type (blank → "Other"), drop ≤0, sort
 * desc, and attach a cycling palette colour + percentage share.
 */
export const allocationByType = (holdings) => {
  const groups = {};
  (holdings || []).forEach((t) => {
    const type = (t.investment_type || "").trim() || "Other";
    groups[type] = (groups[type] || 0) + holdingCashValue(t);
  });

  const rows = Object.keys(groups)
    .map((name) => ({ name, value: groups[name] }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = rows.reduce((sum, r) => sum + r.value, 0);

  return rows.map((r, i) => ({
    name: r.name,
    value: r.value,
    pct: total > 0 ? (r.value / total) * 100 : 0,
    color: ALLOCATION_PALETTE[i % ALLOCATION_PALETTE.length],
  }));
};

/* ------------------------------------------------------------------ *
 * Upcoming maturities (spec §7a)
 * ------------------------------------------------------------------ */

export const maturityDate = (token) => {
  if (!token.maturity) return null;
  const d = new Date(token.maturity);
  return Number.isFinite(d.getTime()) ? d : null;
};

export const daysToMaturity = (token) => {
  const d = maturityDate(token);
  if (!d) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

/**
 * Active-currency holdings with a future maturity, soonest first. Only tokens
 * still held (balance > 0) qualify — a fully-exited position has no principal
 * left to return.
 */
export const upcomingMaturities = (holdings) =>
  (holdings || [])
    .filter((t) => {
      if (holdingShares(t) <= 0) return false;
      const days = daysToMaturity(t);
      return days !== null && days >= 0;
    })
    .sort((a, b) => maturityDate(a).getTime() - maturityDate(b).getTime());

/** Per-share price for a holding — buy price, falling back to exchange. */
export const holdingPrice = (token) => {
  const price = token.price || {};
  return num(price.buy != null ? price.buy : price.exchange);
};

/**
 * Principal returning at maturity = the amount originally invested, NOT the
 * total payout (principal + interest). The app computes "Investment Amount"
 * for an active holding as shares × buy price everywhere (Overview,
 * PayoutBreakdown, TokenTrade), so we match that here. An explicit
 * investmentAmount field (present on the exited/portfolio-history feed) is
 * used as a fallback when price data is missing.
 */
export const principalReturning = (token) => {
  const invested = holdingShares(token) * holdingPrice(token);
  if (invested > 0) return invested;
  if (token.investmentAmount != null) return num(token.investmentAmount);
  return 0;
};

/* ------------------------------------------------------------------ *
 * Portfolio performance (spec §5a)
 * ------------------------------------------------------------------ */

// Period selector → API params (range, interval) + axis labels.
export const PERIODS = [
  { key: "1M", label: "1M", range: "30d", interval: "day", ago: "1M ago" },
  { key: "3M", label: "3M", range: "90d", interval: "week", ago: "3M ago" },
  { key: "6M", label: "6M", range: "180d", interval: "week", ago: "6M ago" },
  { key: "1Y", label: "1Y", range: "1y", interval: "month", ago: "1Y ago" },
  { key: "All", label: "All", range: "all", interval: "month", ago: "Start" },
];

export const periodConfig = (key) =>
  PERIODS.find((p) => p.key === key) || PERIODS[0];

/**
 * Normalise a raw performance response into an ordered array of numbers.
 * Tolerates points shaped as bare numbers or objects with a value-ish field so
 * the chart degrades gracefully if the backend shape shifts.
 */
export const normalizeSeries = (raw) => {
  const points = Array.isArray(raw)
    ? raw
    : (raw && (raw.series || raw.points || raw.data)) || [];
  return (points || []).map((p) => {
    if (typeof p === "number") return p;
    if (!p || typeof p !== "object") return 0;
    return num(p.value ?? p.amount ?? p.total ?? p.usd_value ?? p.balance);
  });
};

/**
 * Current portfolio value: last series point when we have one, else the sum of
 * current holdings' cash value (spec §5a).
 */
export const currentValue = (series, holdings) => {
  if (series && series.length) return series[series.length - 1];
  return totalCashValue(holdings);
};

/* ------------------------------------------------------------------ *
 * Orders (spec §7b)
 * ------------------------------------------------------------------ */

/** Map raw order status to the human label the UI shows. */
export const orderStatusLabel = (status) => {
  switch (status) {
    case "Active":
      return "Pending";
    case "Fulfilled":
      return "Completed";
    case "Terminated":
      return "Cancelled";
    default:
      return status || "";
  }
};

/** Badge colour: green=Completed, orange=Active/Pending, grey=other. */
export const orderStatusColor = (status) => {
  switch (status) {
    case "Fulfilled":
    case "Completed":
      return "#27AE60";
    case "Active":
    case "Pending":
      return "#F2994A";
    default:
      return "#8791A7";
  }
};

// orderAmount lives on `amount` in the web order payload.
export const orderTokens = (order) => num(order && order.amount);
export const orderAmountValue = (order) =>
  orderTokens(order) * num(order && order.price);

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "dd MMM yyyy", e.g. "07 Jul 2026". */
export const formatDayMonthYear = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (!Number.isFinite(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** Plain 2dp number (no symbol) — used for the share count. */
export const shareCount = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num(value));
